import {Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {LearningOutcome} from '../classes/learning-outcome';
import {LocalStorageService} from './local-storage.service';
import {ApiService, RemoteResponse} from './api.service';
import {tap} from 'rxjs/operators';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LearningOutcomesService {
  // This service is responsible for managing learning outcomes
  // and their associated data. It provides methods to add, edit,
  // delete, and retrieve learning outcomes.
  // It also handles the interaction with the API and local storage
  // to persist the data.
  // The service uses signals to manage the state of learning outcomes
  // and provides a readonly signal to access the current state.
  // The service is injected into the components that need to access
  // the learning outcomes data and provides methods to interact with it.

  private learningOutcomesSignal: WritableSignal<LearningOutcome[]> = signal<LearningOutcome[]>([]);
  public learningOutcomes: Signal<LearningOutcome[]> = this.learningOutcomesSignal.asReadonly();


  constructor(
    private localStorage: LocalStorageService,
    private api: ApiService,
  ) {
    // Initialize learning outcomes from local storage
    const learningOutcomes = this.localStorage.getLearningOutcomes();
    if (learningOutcomes.length > 0) {
      this.learningOutcomesSignal.set(learningOutcomes);
    }
    else{
      this.learningOutcomesSignal.set([]);
    }
  }

  public getLearningOutcomesFromApi(response: RemoteResponse): void {
    // Get learning outcomes from the API response
    const learningOutcomes = this.getLearningOutcomesRemoteData(response);
    this.setLearningOutcomes(learningOutcomes);
  }

  private getLearningOutcomesRemoteData(response: RemoteResponse): LearningOutcome[] {
    // Extract learning outcomes from the API response
    const remoteData = response.data[0].dataSet;
    const learningOutcomes: LearningOutcome[] = [];
    remoteData.outcomes?.forEach((outcome: any) => {
      const learningOutcome = new LearningOutcome();
      learningOutcome.setLearningOutcome({
        name: outcome.name,
        requestKey: outcome.requestKey,
      });
      learningOutcomes.push(learningOutcome);
    });
    return learningOutcomes;
  }

  public setLearningOutcomes(learningOutcomes: LearningOutcome[]): void {
    // Set learning outcomes in local storage and update the signal
    this.localStorage.setLearningOutcomes(learningOutcomes);
    this.learningOutcomesSignal.update(() => {
      return [...learningOutcomes];
    });
  }

  public addLearningOutcome(): void {
    // Add a new learning outcome to the list
    const newLearningOutcome = new LearningOutcome();
    this.learningOutcomesSignal.update(learningOutcomes => {
      learningOutcomes.push(newLearningOutcome);
      return [...learningOutcomes];
    });
  }

  // Update the learning outcome in the list
  // and update the learning outcome signal
  public updateLearningOutcomes(learningOutcome: LearningOutcome): void {
    // Update the learning outcome in the list
    const learningOutcomes = this.learningOutcomesSignal();
    const index = learningOutcomes.findIndex(lo => lo.requestKey === learningOutcome.requestKey);
    if (index !== -1) {
      learningOutcomes[index] = learningOutcome;
    }
    else{
      learningOutcomes.push(learningOutcome);
    }
    this.setLearningOutcomes(learningOutcomes);
  }

  // editLearningOutcome method to edit an existing learning outcome
  // and update the learning outcome signal
  // This method should be called from the component
  // where the user edits a learning outcome
  // Needs to return an observable with true or false
  public editLearningOutcome(learningOutcome: LearningOutcome): Observable<RemoteResponse> {
    return this.api.post('saveOutcome', {
      name: learningOutcome.name,
      requestKey: learningOutcome.requestKey,
    }).pipe(
      tap(response => {
        if (response && response.success) {
          if(!learningOutcome.requestKey){
            learningOutcome.requestKey = response.data[0].dataSet.requestKey;
          }
          this.updateLearningOutcomes(learningOutcome);
        }
      }),
    );
  }

  public deleteLearningOutcome(learningOutcome: LearningOutcome): Observable<RemoteResponse> {
    // Delete a learning outcome from the list
    return this.api.post('deleteOutcome', {
      requestKey: learningOutcome.requestKey,
    }).pipe(
      tap(response => {
        if (response && response.success) {
          const learningOutcomes = this.learningOutcomesSignal();
          const index = learningOutcomes.findIndex(lo => lo.requestKey === learningOutcome.requestKey);
          if (index !== -1) {
            learningOutcomes.splice(index, 1);
            this.setLearningOutcomes(learningOutcomes);
          }
        }
      })
    );
  }

}
